import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Boxes, ArrowUpCircle, ArrowDownCircle, Trash2, ShoppingCart, Factory } from "lucide-react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtKg = (n) => (n ?? 0).toLocaleString("it-IT", { maximumFractionDigits: 2 });
const fmtDate = (d) => (d ? d.split("-").reverse().join("/") : "—");

export const WMS_CAUSALE_BADGE = {
  CAR: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100",
  TRC: "bg-teal-100 text-teal-800 hover:bg-teal-100",
  TRS: "bg-purple-100 text-purple-800 hover:bg-purple-100",
  PAT: "bg-red-100 text-red-800 hover:bg-red-100",
  VEN: "bg-blue-100 text-blue-800 hover:bg-blue-100",
};
export const WMS_CAUSALE_LABEL = {
  CAR: "Carico fornitore", TRC: "Carico produzione", TRS: "Trasformazione", PAT: "Scarto", VEN: "Vendita",
};

/* Albero → lista piatta con profondità (il rendering ricorsivo di componenti JSX
   auto-referenzianti non è supportato dalla toolchain di build) */
const flattenTree = (nodes, depth = 0, out = []) => {
  for (const n of nodes || []) {
    out.push({ ...n, depth });
    flattenTree(n.children, depth + 1, out);
  }
  return out;
};

const TraceLine = ({ node }) => {
  const pad = { paddingLeft: `${node.depth * 18}px` };
  if (node.type === "sale") {
    return (
      <div style={pad} className="flex items-start gap-1.5 py-1 text-sm" data-testid="trace-node-sale">
        <ShoppingCart className="w-3.5 h-3.5 text-blue-600 mt-0.5 shrink-0" />
        <span><b>{fmtDate(node.date)}</b> · {node.doc_type} {node.num_mov} → <b>{node.customer_name}</b> — <b className="text-blue-700">{fmtKg(node.qty)} kg</b></span>
      </div>
    );
  }
  if (node.type === "waste") {
    return (
      <div style={pad} className="flex items-start gap-1.5 py-1 text-sm" data-testid="trace-node-waste">
        <Trash2 className="w-3.5 h-3.5 text-red-600 mt-0.5 shrink-0" />
        <span>{fmtDate(node.date)} · Scarto lavorazione — <b className="text-red-700">{fmtKg(node.qty)} kg</b></span>
      </div>
    );
  }
  if (node.type === "transform") {
    return (
      <div style={pad} className="flex items-start gap-1.5 py-1 text-sm" data-testid="trace-node-transform">
        <Factory className="w-3.5 h-3.5 text-purple-600 mt-0.5 shrink-0" />
        <span>
          Lavorato in <b>{node.description || node.product_code}</b>
          <span className="text-xs text-stone-500"> · partita <span className="font-mono">{node.partita}</span>{node.lot ? ` · lotto ${node.lot}` : ""}</span>
          {" — "}prodotti <b className="text-purple-700">{fmtKg(node.qty_produced)} kg</b>
          <span className="text-xs text-stone-500"> (giacenza {fmtKg(node.balance)} kg)</span>
        </span>
      </div>
    );
  }
  return (
    <div style={pad} className="flex items-start gap-1.5 py-1 text-sm" data-testid="trace-node-up">
      <ArrowUpCircle className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
      <span>
        Da <b>{node.description || node.product_code}</b>
        <span className="text-xs text-stone-500"> · partita <span className="font-mono">{node.partita}</span>{node.lot ? ` · lotto ${node.lot}` : ""}</span>
        {" — "}consumati <b className="text-emerald-700">{fmtKg(node.qty_consumed)} kg</b>
        {node.origin === "supplier" && node.supplier_name && <span className="text-xs text-stone-500"> · fornitore <b>{node.supplier_name}</b></span>}
      </span>
    </div>
  );
};

const PartitaInfoHeader = ({ info }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
    <div>
      <p className="text-xs text-stone-500">Articolo</p>
      <p className="font-semibold">{info.description || info.product_code}</p>
      <p className="font-mono text-xs text-stone-500">{info.product_code}</p>
    </div>
    <div>
      <p className="text-xs text-stone-500">Origine</p>
      <p className="font-semibold">{info.origin === "supplier" ? (info.supplier_name || "Fornitore") : "Produzione interna"}</p>
      {info.expiry_date && <p className="text-xs text-stone-500">Scad. {fmtDate(info.expiry_date)}</p>}
    </div>
    <div>
      <p className="text-xs text-stone-500">Caricato / Scaricato</p>
      <p className="font-semibold">{fmtKg(info.qty_in)} / {fmtKg(info.qty_out)} kg</p>
    </div>
    <div>
      <p className="text-xs text-stone-500">Giacenza</p>
      <p className={`font-bold text-lg ${info.balance > 0.001 ? "text-emerald-700" : "text-stone-400"}`} data-testid="wms-detail-balance">{fmtKg(info.balance)} kg</p>
    </div>
  </div>
);

const PartitaMovementsTable = ({ movements }) => (
  <Card className="bg-white border-stone-200 overflow-hidden">
    <div className="p-3 border-b border-stone-200"><p className="font-semibold text-sm">📋 Movimenti della partita ({movements.length})</p></div>
    <div className="overflow-x-auto max-h-[40vh] overflow-y-auto">
      <table className="w-full text-sm min-w-[620px]">
        <thead className="bg-stone-50 sticky top-0">
          <tr>
            <th className="text-left p-2 text-xs font-semibold text-stone-600">Data</th>
            <th className="text-left p-2 text-xs font-semibold text-stone-600">Tipo</th>
            <th className="text-left p-2 text-xs font-semibold text-stone-600">Cliente / Fornitore</th>
            <th className="text-right p-2 text-xs font-semibold text-stone-600">Kg +</th>
            <th className="text-right p-2 text-xs font-semibold text-stone-600">Kg −</th>
            <th className="text-left p-2 text-xs font-semibold text-stone-600">Note</th>
          </tr>
        </thead>
        <tbody>
          {movements.map((m, i) => (
            <tr key={i} className="border-t border-stone-100" data-testid={`wms-detail-mov-${i}`}>
              <td className="p-2 whitespace-nowrap">{fmtDate(m.date)}</td>
              <td className="p-2"><Badge className={WMS_CAUSALE_BADGE[m.causale]}>{WMS_CAUSALE_LABEL[m.causale] || m.movement}</Badge></td>
              <td className="p-2 truncate max-w-[200px]">{m.customer_name || m.supplier_name || "—"}</td>
              <td className="p-2 text-right text-emerald-700 font-semibold">{m.qty_in > 0 ? fmtKg(m.qty_in) : ""}</td>
              <td className="p-2 text-right text-red-700 font-semibold">{m.qty_out > 0 ? fmtKg(m.qty_out) : ""}</td>
              <td className="p-2 text-xs text-stone-500 truncate max-w-[160px]">{m.note || ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Card>
);

const WmsPartitaDialog = ({ partita, onClose }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/wms/trace`, { params: { partita } })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Errore caricamento tracciabilità"));
  }, [partita]);
  const info = data?.info;
  const upstreamFlat = data ? flattenTree(data.upstream) : [];
  const downstreamFlat = data ? flattenTree(data.downstream) : [];
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="wms-partita-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            <Boxes className="w-5 h-5 text-[#8A1C2A]" />
            Partita <span className="font-mono">{partita}</span>
            {info?.lot && <Badge className="bg-[#C5A059]/15 text-[#8a6d2f] hover:bg-[#C5A059]/15">Lotto {info.lot}</Badge>}
            {info?.orphan && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">CARICO NON IMPORTATO</Badge>}
          </DialogTitle>
        </DialogHeader>
        {!data ? <p className="p-4 text-sm text-stone-500">Caricamento…</p> : (
          <div className="space-y-4">
            <PartitaInfoHeader info={info} />
            {upstreamFlat.length > 0 && (
              <Card className="bg-white border-stone-200 p-3" data-testid="wms-trace-upstream">
                <p className="font-semibold text-sm mb-1 flex items-center gap-1.5"><ArrowUpCircle className="w-4 h-4 text-emerald-600" /> Origine (da cosa è stata prodotta)</p>
                {upstreamFlat.map((n, i) => <TraceLine key={i} node={n} />)}
              </Card>
            )}
            {downstreamFlat.length > 0 && (
              <Card className="bg-white border-stone-200 p-3" data-testid="wms-trace-downstream">
                <p className="font-semibold text-sm mb-1 flex items-center gap-1.5"><ArrowDownCircle className="w-4 h-4 text-blue-600" /> Destinazione (dove è finita)</p>
                {downstreamFlat.map((n, i) => <TraceLine key={i} node={n} />)}
              </Card>
            )}
            <PartitaMovementsTable movements={data.movements} />
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose} data-testid="wms-detail-close">Chiudi</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WmsPartitaDialog;
